// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/contextMemoryBadges.test.ts
import { createContext, useContext, type ReactNode } from "react";

import { groupRailCitations } from "./groupRailCitations";

import { type MemoryCitation } from "../../utils/memoryCitationFields";

/** A saved note the Context pane numbers, and the number on its card. */
export type ContextMemoryBadge = {
  id: string;
  text: string;
  number: number;
};

const EMPTY: readonly ContextMemoryBadge[] = [];

const ContextMemoryBadgesContext =
  createContext<readonly ContextMemoryBadge[]>(EMPTY);

/**
 * The M badges the Context pane is showing for this answer.
 *
 * The chat's saved-memory card reads this so it can wear the same number as
 * the card in the pane. Outside a pane (the style guide, a unit render) the
 * list is empty and the card draws no badge.
 */
export function ContextMemoryBadgeProvider({
  badges,
  children,
}: {
  badges: readonly ContextMemoryBadge[];
  children: ReactNode;
}) {
  return (
    <ContextMemoryBadgesContext.Provider value={badges}>
      {children}
    </ContextMemoryBadgesContext.Provider>
  );
}

export function useContextMemoryBadges(): readonly ContextMemoryBadge[] {
  return useContext(ContextMemoryBadgesContext);
}

export type NumberedContextMemory = {
  memory: MemoryCitation & { supportText: string };
  number: number;
};

/**
 * The numbered memory cards the rail draws, in that order.
 *
 * why one list: the pane and the chat card both read this, so a memory with
 * no supporting sentence stays a plain row and neither side invents an M number.
 */
export function numberedContextMemories(
  citedClaims: readonly unknown[] | undefined,
  memoryNumbers: ReadonlyMap<string, number> | undefined,
): NumberedContextMemory[] {
  const memories = groupRailCitations(citedClaims).memories.filter(
    (memory): memory is MemoryCitation & { supportText: string } =>
      memory.text.trim() !== "" && !!memory.supportText,
  );
  return memories
    .map((memory, position) => ({
      memory,
      number: memoryNumbers?.get(memory.id) ?? position + 1,
    }))
    .sort((a, b) => a.number - b.number);
}

export function contextMemoryBadges(
  citedClaims: readonly unknown[] | undefined,
  memoryNumbers: ReadonlyMap<string, number> | undefined,
): ContextMemoryBadge[] {
  return numberedContextMemories(citedClaims, memoryNumbers).map(
    ({ memory, number }) => ({
      id: memory.id,
      text: memory.text,
      number,
    }),
  );
}

function normalizeMemoryText(value: string): string {
  // why: a pre-schema edge stores warhol's 120-character label plus an ellipsis, and that mark is not part of the saved note.
  return value
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\u2026$/, "");
}

/**
 * The Context pane's number for this saved instruction, or none when the
 * pane is not showing that note.
 *
 * A short overlap is not a match: "Ideal" appears in many notes. One text
 * has to contain the other, and the shorter one has to be long enough to be
 * the note itself.
 */
export function badgeNumberForInstruction(
  instruction: string,
  badges: readonly ContextMemoryBadge[],
  memoryId?: string,
): number | undefined {
  if (memoryId) {
    const byId = badges.find((badge) => badge.id === memoryId);
    if (byId) return byId.number;
  }
  const needle = normalizeMemoryText(instruction);
  if (needle.length < 24) return undefined;
  const hits = badges.filter((badge) => {
    const hay = normalizeMemoryText(badge.text);
    if (hay.length < 24) return false;
    return hay === needle || hay.includes(needle) || needle.includes(hay);
  });
  if (hits.length === 0) return undefined;
  const longest = Math.max(
    ...hits.map((badge) => normalizeMemoryText(badge.text).length),
  );
  const best = hits.filter(
    (badge) => normalizeMemoryText(badge.text).length === longest,
  );
  // why: two clipped labels can share one opening, and the first card is not this note.
  if (best.length !== 1) return undefined;
  return best[0].number;
}
